// src/Component/PortalDropdown.js
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/**
 * PortalDropdown
 * --------------
 * A searchable single-select dropdown whose menu is rendered into
 * document.body via ReactDOM.createPortal(). Because the menu is not a
 * descendant of the trigger's DOM tree, no ancestor `overflow: hidden /
 * auto / scroll` can ever clip it.
 *
 * Props
 * -----
 *   options        Array<{ value, label, ...rest }>
 *   selectedValue  string  (label of currently-selected option)
 *   onSelect       (option | null) => void
 *   placeholder    string
 *   isSearchable   boolean
 *   isClearable    boolean
 *   disabled       boolean
 *   loading        boolean
 *   minMenuWidth   number  (px) — default 220
 *   maxMenuHeight  number  (px) — default 260
 */
export default function PortalDropdown({
  options = [],
  selectedValue = "",
  onSelect,
  placeholder = "Select...",
  isSearchable = true,
  isClearable = true,
  disabled = false,
  loading = false,
  minMenuWidth = 220,
  maxMenuHeight = 260,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState({});

  // ---- Filter options by query ----
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((opt) =>
      String(opt.label ?? "").toLowerCase().includes(q)
    );
  }, [options, query, isSearchable]);

  // ---- Compute menu position ----
  const computePosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, minMenuWidth);
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    // Prefer opening below; flip up if not enough room.
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < maxMenuHeight && spaceAbove > spaceBelow;

    // Horizontal: keep the menu inside the viewport.
    let left = rect.left;
    if (left + width > viewportW - 8) {
      left = Math.max(8, viewportW - width - 8);
    }

    const top = openUp
      ? Math.max(8, rect.top - Math.min(spaceAbove, maxMenuHeight) - 4)
      : rect.bottom + 4;

    setMenuStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight: Math.min(maxMenuHeight, openUp ? spaceAbove - 8 : spaceBelow - 8),
      zIndex: 2147483000, // near max int — paints above everything
    });
  };

  // ---- Open / close handlers ----
  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
  };

  const toggleMenu = () => {
    if (disabled) return;
    if (open) closeMenu();
    else openMenu();
  };

  // ---- Position on open, and keep in sync while open ----
  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onScrollOrResize = () => computePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- Focus search when menu opens ----
  useEffect(() => {
    if (open && isSearchable && searchRef.current) {
      // small delay so the element is actually mounted & visible
      const id = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open, isSearchable]);

  // ---- Close on outside click / Escape ----
  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      closeMenu();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // ---- Selection ----
  const handleSelect = (option) => {
    onSelect?.(option);
    closeMenu();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect?.(null);
    closeMenu();
  };

  // ---- Render ----
  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          className="pd-menu"
          style={menuStyle}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isSearchable && (
            <div className="pd-search-wrap">
              <input
                ref={searchRef}
                className="pd-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
              />
            </div>
          )}

          <div className="pd-options" role="listbox">
            {loading && (
              <div className="pd-empty">Loading…</div>
            )}

            {!loading && filteredOptions.length === 0 && (
              <div className="pd-empty">No options</div>
            )}

            {!loading &&
              filteredOptions.map((opt, idx) => {
                const isSelected =
                  String(opt.label) === String(selectedValue);
                return (
                  <div
                    key={opt.value ?? opt.id ?? `${opt.label}-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    className={`pd-option ${isSelected ? "is-selected" : ""}`}
                    onClick={() => handleSelect(opt)}
                  >
                    {opt.label}
                  </div>
                );
              })}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div
        ref={triggerRef}
        className={`pd-trigger ${disabled ? "is-disabled" : ""} ${
          open ? "is-open" : ""
        }`}
        onClick={toggleMenu}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleMenu();
          }
        }}
      >
        <span className={`pd-value ${selectedValue ? "" : "is-placeholder"}`}>
          {selectedValue || placeholder}
        </span>

        <span className="pd-icons">
          {isClearable && selectedValue && !disabled && (
            <span
              className="pd-clear"
              onClick={handleClear}
              title="Clear"
            >
              ×
            </span>
          )}
          <span className="pd-caret">▾</span>
        </span>
      </div>

      {menu}

      {/* Scoped styles — safe to keep here for a drop-in component. */}
      <style>{`
        .pd-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          min-height: 30px;
          padding: 4px 8px;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          background: #fff;
          color: #172033;
          font-size: 11px;
          cursor: pointer;
          user-select: none;
          outline: none;
        }
        .pd-trigger:hover { border-color: #94a3b8; }
        .pd-trigger:focus,
        .pd-trigger.is-open { border-color: #1461bd; box-shadow: 0 0 0 2px rgba(20,97,189,.12); }
        .pd-trigger.is-disabled { background: #f1f5f9; cursor: not-allowed; opacity: .7; }
        .pd-value {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .pd-value.is-placeholder { color: #94a3b8; }
        .pd-icons { display: inline-flex; align-items: center; gap: 4px; color: #64748b; }
        .pd-clear {
          display: inline-flex; align-items: center; justify-content: center;
          width: 16px; height: 16px; border-radius: 50%;
          font-size: 13px; line-height: 1;
        }
        .pd-clear:hover { background: #e2e8f0; color: #0f172a; }
        .pd-caret { font-size: 10px; }

        /* The menu is portaled into <body>, so no ancestor can clip it. */
        .pd-menu {
          background: #fff;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          box-shadow: 0 12px 32px rgba(15, 23, 42, .18);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-width: 200px;
        }
        .pd-search-wrap {
          padding: 6px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .pd-search {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 5px 8px;
          font-size: 12px;
          outline: none;
        }
        .pd-search:focus { border-color: #1461bd; }
        .pd-options {
          overflow-y: auto;
          max-height: 220px;
        }
        .pd-option {
          padding: 7px 10px;
          font-size: 12px;
          color: #172033;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pd-option:hover { background: #eff6ff; }
        .pd-option.is-selected { background: #dbeafe; color: #0b3d91; font-weight: 600; }
        .pd-empty {
          padding: 12px;
          text-align: center;
          color: #94a3b8;
          font-size: 12px;
          font-style: italic;
        }
      `}</style>
    </>
  );
}